import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  X,
} from 'lucide-react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import useSEO from '../hooks/useSEO';
import CircleWordmark from '../components/CircleWordmark';
import { CIRCLE_CATEGORIES, categoryLabel } from '../content/circleCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISPLAY_LIMIT = 10; // how many profiles show on /cercle per page load

// Fisher-Yates shuffle — returns a new array, never mutates input.
const shuffle = (arr) => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

// Tonal gradient palette used as a fallback when a member has no photo.
// Indexed by a deterministic hash of their name so the same person always
// gets the same colour family across sessions.
const TONAL_GRADIENTS = [
  'linear-gradient(160deg,#9B7B55 0%,#6B4A35 45%,#3d2215 100%)',
  'linear-gradient(160deg,#4a5a6a 0%,#2d3d4d 45%,#151e28 100%)',
  'linear-gradient(160deg,#7a6040 0%,#4a3820 45%,#2a1e0c 100%)',
  'linear-gradient(160deg,#2d5a40 0%,#1a3d2a 45%,#0a1e14 100%)',
  'linear-gradient(160deg,#5a5040 0%,#3c3530 45%,#1c1814 100%)',
  'linear-gradient(160deg,#2c3e50 0%,#1c2c3c 45%,#0c1620 100%)',
];

const hashString = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const initialsOf = (fullName = '') =>
  fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

const ClientelePage = () => {
  const { t, language } = useLanguage();
  useSEO({ title: t.seo.clientele.title, description: t.seo.clientele.description });

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null); // member shown in profile sheet
  const [formOpen, setFormOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(''); // '' = all
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    profession: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/clientele/public`);
        if (!cancelled) setMembers(data || []);
      } catch (e) {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Lock body scroll while the profile sheet or form modal is open
  useEffect(() => {
    const lock = activeIndex !== null || formOpen;
    document.body.style.overflow = lock ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeIndex, formOpen]);

  // Filter by category first, then shuffle, then cap at DISPLAY_LIMIT.
  // useMemo keys on `members` + `selectedCategory` so the grid refreshes
  // on every page load (new shuffle each time members arrive) and also
  // when the admin dropdown changes.
  const displayedMembers = useMemo(() => {
    const pool = selectedCategory
      ? members.filter((m) => m.category === selectedCategory)
      : members;
    return shuffle(pool).slice(0, DISPLAY_LIMIT);
  }, [members, selectedCategory]);

  // Categories that actually have at least one live member — hides filters
  // that would lead to an empty view.
  const populatedCategories = useMemo(() => {
    const set = new Set(members.map((m) => m.category).filter(Boolean));
    return CIRCLE_CATEGORIES.filter((c) => set.has(c));
  }, [members]);

  const activeMember =
    activeIndex !== null ? displayedMembers[activeIndex] : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clientele/request`, formData);
      setSubmitted(true);
      setFormData({ full_name: '', email: '', profession: '' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Le Cercle submit failed', err);
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    // delay submitted reset so the success view doesn't flicker on re-open
    setTimeout(() => setSubmitted(false), 200);
  };

  return (
    <div className="min-h-screen bg-[#F5F0E8]" data-testid="clientele-page">
      {/* ============================================ */}
      {/* HERO — Forest green with inline wordmark     */}
      {/* ============================================ */}
      <section className="relative bg-[#25342C] pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="block w-8 h-px bg-[#A14223]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#F5F0E8]/55">
                {t.clientele.eyebrow}
              </span>
            </div>

            {/* Title row — H1 with the wax-seal wordmark inline beside it */}
            <div className="flex items-center gap-5 lg:gap-7 flex-wrap">
              <h1
                className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#F5F0E8] leading-[0.95]"
                data-testid="clientele-title"
              >
                {t.clientele.title}
              </h1>
              <motion.div
                initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                className="shrink-0"
              >
                <CircleWordmark variant="parchment" size={84} />
              </motion.div>
            </div>

            <p className="mt-3 mb-7 font-serif italic text-base lg:text-lg text-[#F5F0E8]/55 tracking-[0.18em] uppercase">
              {t.clientele.titleSuffix}
            </p>
            <p className="font-light text-base lg:text-lg leading-relaxed text-[#F5F0E8]/65 max-w-xl">
              {t.clientele.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MEMBERS GRID — with categories filter         */}
      {/* ============================================ */}
      <section className="py-14 lg:py-20 bg-[#F5F0E8]">
        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <span className="block w-7 h-px bg-[#A14223]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#5a5550]">
                {t.clientele.membersEyebrow}
              </span>
            </div>

            {/* Categories dropdown — hidden while no members exist yet */}
            {!loading && members.length > 0 && (
              <div className="flex items-center gap-3">
                <label
                  htmlFor="cercle-category"
                  className="text-[10px] uppercase tracking-[0.28em] text-[#5a5550] hidden sm:block"
                >
                  {t.clientele.categoryLabel}
                </label>
                <div className="relative">
                  <select
                    id="cercle-category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-[#5a5550]/25 text-[#25342C] text-xs uppercase tracking-[0.18em] font-light focus:outline-none focus:border-[#A14223] cursor-pointer min-w-[220px]"
                    data-testid="cercle-category-select"
                  >
                    <option value="">{t.clientele.categoryAll}</option>
                    {populatedCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {categoryLabel(cat, language)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A14223] pointer-events-none"
                  />
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="aspect-[3/4] bg-[#25342C]/10 animate-pulse"
                />
              ))}
            </div>
          ) : displayedMembers.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
              {displayedMembers.map((m, idx) => {
                const name = m.display_name || m.full_name;
                const title = m.title || m.profession;
                const initials = initialsOf(name);
                const gradient =
                  TONAL_GRADIENTS[hashString(name) % TONAL_GRADIENTS.length];
                const bg = m.photo_url ? `url(${m.photo_url})` : gradient;
                return (
                  <motion.div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveIndex(idx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveIndex(idx);
                      }
                    }}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5, delay: Math.min(idx * 0.06, 0.4) }}
                    whileHover={{ y: -3 }}
                    className="group relative aspect-[3/4] overflow-hidden text-left bg-[#25342C] rounded-[2px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A14223] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5F0E8]"
                    data-testid={`member-${m.id}`}
                    aria-label={`${name} — ${title}`}
                  >
                    <div
                      className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.04]"
                      style={{
                        backgroundImage: bg,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center top',
                      }}
                    />
                    {/* Initials watermark only when no photo */}
                    {!m.photo_url && (
                      <span
                        aria-hidden
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] font-serif font-light text-[44px] tracking-[0.08em] text-white/15 select-none"
                      >
                        {initials}
                      </span>
                    )}
                    {/* Bottom blur veil */}
                    <span
                      aria-hidden
                      className="absolute bottom-0 left-0 right-0 h-[62%] pointer-events-none"
                      style={{
                        backgroundImage:
                          'linear-gradient(to bottom, transparent 0%, rgba(30,42,33,0.55) 40%, rgba(20,30,22,0.92) 100%)',
                      }}
                    />

                    {/* Category badge — clickable: tapping it filters the
                        grid to that category instead of opening the profile. */}
                    {m.category && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(m.category);
                          // Scroll the members section back into view so
                          // the user immediately sees the filtered result.
                          document
                            .getElementById('cercle-category')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        className="absolute top-2.5 right-2.5 lg:top-3 lg:right-3 px-2 py-1 text-[9px] lg:text-[10px] uppercase tracking-[0.14em] font-light bg-[#A14223]/90 text-[#F5F0E8] backdrop-blur-sm max-w-[75%] truncate hover:bg-[#A14223] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F0E8] transition-colors"
                        data-testid={`member-category-${m.id}`}
                        aria-label={`Filter by ${categoryLabel(m.category, language)}`}
                      >
                        {categoryLabel(m.category, language)}
                      </button>
                    )}

                    {/* Caption */}
                    <span className="absolute left-0 right-0 bottom-0 p-3 lg:p-4">
                      <span className="block font-serif text-[15px] lg:text-base text-[#F5F0E8] tracking-wide truncate">
                        {name}
                      </span>
                      <span className="block mt-1 text-[10px] uppercase tracking-[0.18em] text-[#A14223] truncate">
                        {title}
                      </span>
                    </span>
                  </motion.div>
                );
              })}
            </div>
          ) : members.length > 0 && selectedCategory ? (
            // Category filter yields nothing — offer a way back.
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border border-[#5a5550]/20 px-8 py-14 text-center"
              data-testid="clientele-no-match"
            >
              <p className="font-serif italic text-xl lg:text-2xl text-[#25342C] mb-4">
                {t.clientele.noCategoryMatchTitle}
              </p>
              <p className="text-sm text-[#5a5550] font-light max-w-md mx-auto leading-relaxed mb-6">
                {t.clientele.noCategoryMatchBody}
              </p>
              <button
                type="button"
                onClick={() => setSelectedCategory('')}
                className="btn-outline text-xs uppercase tracking-[0.2em]"
                data-testid="cercle-category-clear"
              >
                {t.clientele.categoryAll}
              </button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border border-[#5a5550]/20 px-8 py-16 text-center"
              data-testid="clientele-empty"
            >
              <div className="flex justify-center mb-5">
                <CircleWordmark variant="forest" size={120} />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#25342C] mb-2">
                {t.clientele.emptyTitle}
              </h3>
              <p className="text-sm text-[#5a5550] font-light max-w-md mx-auto leading-relaxed">
                {t.clientele.emptyBody}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* ============================================ */}
      {/* CTA BAND                                     */}
      {/* ============================================ */}
      <section className="bg-[#25342C] py-16 lg:py-20" data-testid="clientele-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl lg:text-5xl text-[#F5F0E8] mb-4 tracking-tight">
            {t.clientele.ctaTitle}
          </h2>
          <p className="font-light text-sm lg:text-base text-[#F5F0E8]/60 leading-relaxed max-w-xl mx-auto mb-8">
            {t.clientele.note}
          </p>
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="btn-cta inline-flex items-center gap-3"
            data-testid="show-form-button"
          >
            {t.clientele.ctaButton}
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ============================================ */}
      {/* PROFILE SHEET                                */}
      {/* ============================================ */}
      <AnimatePresence>
        {activeMember && (
          <motion.div
            key="profile-sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-[#F5F0E8] overflow-y-auto"
            data-testid="member-profile"
          >
            <ProfileSheet
              member={activeMember}
              tx={t.clientele}
              language={language}
              onClose={() => setActiveIndex(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============================================ */}
      {/* REQUEST FORM MODAL                           */}
      {/* ============================================ */}
      <AnimatePresence>
        {formOpen && (
          <motion.div
            key="form-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center"
            onClick={closeForm}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="w-full lg:max-w-lg bg-[#25342C] text-[#F5F0E8] p-7 lg:p-10 max-h-[92vh] overflow-y-auto relative"
              data-testid="request-form-modal"
            >
              <button
                type="button"
                onClick={closeForm}
                className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#F5F0E8]/25 flex items-center justify-center hover:border-[#A14223] transition-colors"
                aria-label="Close"
                data-testid="form-close"
              >
                <X size={16} />
              </button>

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-10"
                  data-testid="form-success"
                >
                  <CheckCircle className="w-16 h-16 mx-auto text-[#F5F0E8] mb-5" />
                  <p className="font-serif text-2xl mb-7">{t.clientele.form.success}</p>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="btn-outline btn-outline-light"
                  >
                    {t.clientele.profileBack}
                  </button>
                </motion.div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="block w-7 h-px bg-[#A14223]" />
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#F5F0E8]/55">
                      {t.clientele.eyebrow}
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl lg:text-4xl mb-2 leading-tight">
                    {t.clientele.requestTitle}
                  </h3>
                  <p className="text-xs lg:text-sm text-[#F5F0E8]/55 font-light mb-7 leading-relaxed">
                    {t.clientele.note}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="clientele-request-form">
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder={t.clientele.form.name}
                      required
                      className="form-input-editorial bg-transparent text-[#F5F0E8] border-[#F5F0E8]/30 placeholder-[#F5F0E8]/40"
                      data-testid="input-name"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.clientele.form.email}
                      required
                      autoComplete="email"
                      className="form-input-editorial bg-transparent text-[#F5F0E8] border-[#F5F0E8]/30 placeholder-[#F5F0E8]/40"
                      data-testid="input-email"
                    />
                    <input
                      type="text"
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      placeholder={t.clientele.form.profession}
                      required
                      className="form-input-editorial bg-transparent text-[#F5F0E8] border-[#F5F0E8]/30 placeholder-[#F5F0E8]/40"
                      data-testid="input-profession"
                    />
                    <button
                      type="submit"
                      className="btn-cta w-full flex items-center justify-center gap-3"
                      data-testid="submit-request"
                    >
                      {t.clientele.form.submit}
                      <ArrowRight size={16} />
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// =================================================
// PROFILE SHEET — full-screen detail view
// =================================================
const ProfileSheet = ({ member, tx, language, onClose }) => {
  const name = member.display_name || member.full_name;
  const title = member.title || member.profession;

  const initials = initialsOf(name);
  const gradient = useMemo(
    () => TONAL_GRADIENTS[hashString(name) % TONAL_GRADIENTS.length],
    [name]
  );
  const bg = member.photo_url ? `url(${member.photo_url})` : gradient;

  // Subtle Ken-Burns / parallax effect on the sticky portrait. scrollRef
  // is attached to the motion wrapper below; framer watches it inside
  // its nearest scroll ancestor (the ProfileSheet's overflow container).
  const scrollRef = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ['start start', 'end end'],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const photoScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // The four canonical sections come straight from the questionnaire. If
  // a member is from the legacy submission flow (no questionnaire yet),
  // fall back to whatever they typed in the intake "specialty" field
  // for the About section so the page never shows blank panels.
  const sections = [
    { key: 'at_casanova',          label: tx.profileAtCasanova,         text: member.at_casanova },
    { key: 'about',                label: tx.profileAbout,              text: member.about || member.specialty },
    { key: 'profession_specialty', label: tx.profileProfessionSpecialty,text: member.profession_specialty },
    { key: 'connect',              label: tx.profileConnect,            text: member.connect || member.social_link },
  ].filter((s) => s.text);

  return (
    <motion.div
      ref={scrollRef}
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 24, opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="min-h-screen bg-[#F5F0E8]"
    >
      {/* Global top bar — sits above both columns */}
      <div className="sticky top-0 z-10 bg-[#F5F0E8]/95 backdrop-blur-sm border-b border-[#5a5550]/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-10 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#5a5550] hover:text-[#25342C] transition-colors"
            data-testid="profile-back"
          >
            <ArrowLeft size={14} />
            {tx.profileBack}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#5a5550]/30 flex items-center justify-center text-[#5a5550] hover:border-[#A14223] hover:text-[#A14223] transition-colors"
            aria-label="Close"
            data-testid="profile-close"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Two-column body: image left, content right (stacks on mobile) */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 lg:gap-10 px-5 lg:px-10 pt-6 lg:pt-10 pb-20">
        {/* LEFT — portrait column */}
        <div className="lg:col-span-5 xl:col-span-5 mb-8 lg:mb-0">
          <div className="lg:sticky lg:top-24">
            <div className="relative aspect-[3/4] overflow-hidden bg-[#25342C] shadow-sm">
              <motion.div
                className="absolute inset-0 will-change-transform"
                style={{
                  backgroundImage: bg,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center top',
                  y: photoY,
                  scale: photoScale,
                }}
              />
              {!member.photo_url && (
                <span
                  aria-hidden
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif font-light text-[90px] tracking-[0.1em] text-white/15 select-none"
                >
                  {initials}
                </span>
              )}
            </div>

            {/* Meta block under portrait */}
            <div className="mt-5">
              {member.category && (
                <span
                  className="inline-block mb-4 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-light bg-[#A14223] text-[#F5F0E8]"
                  data-testid="profile-category-badge"
                >
                  {categoryLabel(member.category, language)}
                </span>
              )}
              <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-[#25342C] leading-[1.05] tracking-tight">
                {name}
              </h2>
              <p className="mt-3 text-[11px] lg:text-xs uppercase tracking-[0.22em] text-[#A14223]">
                {title}
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT — four-section body */}
        <div className="lg:col-span-7 xl:col-span-7 lg:pt-4">
          {sections.length > 0 ? (
            sections.map((s) => (
              <Section key={s.key} label={s.label}>
                <p className="font-light text-[15px] lg:text-base leading-[1.85] text-[#3d3d38] whitespace-pre-line">
                  {s.text}
                </p>
              </Section>
            ))
          ) : (
            <p className="text-sm font-light text-[#5a5550] italic">
              {tx.profileNoSocial}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Section = ({ label, children }) => (
  <div className="mb-9">
    <div className="flex items-center gap-3 mb-3">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#A14223]">{label}</span>
      <span className="flex-1 h-px bg-[#5a5550]/20" />
    </div>
    {children}
  </div>
);

export default ClientelePage;
